/**
 * Data Health Dashboard Widget
 * Displays key metrics across all claims for data quality monitoring
 */
import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Brain, 
  AlertTriangle, 
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '../../utils/api';

interface DataHealthMetrics {
  categorization: {
    total_transactions: number;
    auto_categorized: number;
    auto_categorized_pct: number;
    manual_categorized: number;
    manual_categorized_pct: number;
    uncategorized: number;
    uncategorized_pct: number;
  };
  ai_confidence: {
    average_score: number;
    samples_count: number;
    grade: string;
  };
  response_time: {
    average_hours: number;
    average_days: number;
    samples_count: number;
    grade: string;
  };
  stale_clarifications: {
    total_pending: number;
    stale_count: number;
    stale_pct: number;
    claims_affected: number;
    threshold_days: number;
  };
  anomalies: {
    total_count: number;
    top_types: Array<{ type: string; count: number }>;
  };
  ai_workflow: {
    ai_parsed_requests: number;
    ai_approved_requests: number;
    approval_rate: number;
  };
  generated_at: string;
}

interface DataHealthWidgetProps {
  reportId?: string;
  compact?: boolean;
}

export default function DataHealthWidget({ reportId, compact = false }: DataHealthWidgetProps) {
  const [metrics, setMetrics] = useState<DataHealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      const data = await api.get<DataHealthMetrics>('/api/v1/analytics/data-health', { showErrorToast: false });
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getGradeColor = (grade: string) => {
    switch (grade.toLowerCase()) {
      case 'excellent':
      case 'fast':
        return 'text-emerald-600 bg-emerald-50';
      case 'good':
        return 'text-blue-600 bg-blue-50';
      case 'fair':
      case 'slow':
        return 'text-amber-600 bg-amber-50';
      default:
        return 'text-red-600 bg-red-50';
    }
  };

  if (loading && !metrics) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={fetchMetrics} className="ml-auto text-sm text-blue-600 hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="data-health-widget">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Data Health Dashboard</h3>
            <p className="text-xs text-gray-500">Key metrics across all claims</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); fetchMetrics(); }}
            className={`p-2 rounded-lg hover:bg-gray-100 transition ${refreshing ? 'animate-spin' : ''}`}
            title="Refresh metrics"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Key Metrics - Clean vertical list for narrow containers */}
          <div className="space-y-3">
            {/* Auto-Categorization */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm text-slate-600">Auto-Categorized</span>
              </div>
              <span className="text-sm font-semibold text-emerald-600">
                {metrics.categorization.auto_categorized_pct}%
              </span>
            </div>

            {/* AI Confidence */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-sm text-slate-600">AI Confidence</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(metrics.ai_confidence.grade)}`}>
                  {metrics.ai_confidence.grade}
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {metrics.ai_confidence.average_score}%
                </span>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-sm text-slate-600">Avg Response</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-xs px-1.5 py-0.5 rounded ${getGradeColor(metrics.response_time.grade)}`}>
                  {metrics.response_time.grade}
                </span>
                <span className="text-sm font-semibold text-amber-600">
                  {metrics.response_time.average_days < 1 
                    ? `${Math.round(metrics.response_time.average_hours)}h`
                    : `${metrics.response_time.average_days}d`
                  }
                </span>
              </div>
            </div>

            {/* Stale Items */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${metrics.stale_clarifications.stale_pct > 20 ? 'bg-red-500' : 'bg-slate-400'}`} />
                <span className="text-sm text-slate-600">Stale (7+ days)</span>
              </div>
              <span className={`text-sm font-semibold ${metrics.stale_clarifications.stale_pct > 20 ? 'text-red-600' : 'text-slate-500'}`}>
                {metrics.stale_clarifications.stale_count}/{metrics.stale_clarifications.total_pending}
              </span>
            </div>
          </div>

          {/* Categorization Progress Bar */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span>Categorization</span>
              <span>{metrics.categorization.total_transactions.toLocaleString()} txns</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
              <div 
                className="bg-emerald-500 transition-all"
                style={{ width: `${metrics.categorization.auto_categorized_pct}%` }}
                title={`Auto: ${metrics.categorization.auto_categorized_pct}%`}
              />
              <div 
                className="bg-blue-500 transition-all"
                style={{ width: `${metrics.categorization.manual_categorized_pct}%` }}
                title={`Manual: ${metrics.categorization.manual_categorized_pct}%`}
              />
              <div 
                className="bg-amber-400 transition-all"
                style={{ width: `${metrics.categorization.uncategorized_pct}%` }}
                title={`Uncategorized: ${metrics.categorization.uncategorized_pct}%`}
              />
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Auto</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Manual</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Pending</span>
            </div>
          </div>

          {/* AI Workflow Stats - Compact */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span className="flex items-center gap-1">
                <Brain className="w-3 h-3" />
                AI Workflow
              </span>
              <span className="text-blue-600 font-medium">{metrics.ai_workflow.approval_rate}% approved</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">{metrics.ai_workflow.ai_parsed_requests} parsed</span>
              <span className="text-emerald-600">{metrics.ai_workflow.ai_approved_requests} approved</span>
            </div>
          </div>

          {/* Anomalies - Only show if there are any */}
          {metrics.anomalies.total_count > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span>Anomalies Detected</span>
                <span className="text-amber-600 font-medium">{metrics.anomalies.total_count}</span>
              </div>
              {metrics.anomalies.top_types.slice(0, 2).map((anomaly, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-slate-500 truncate flex-1 pr-2">{anomaly.type}</span>
                  <span className="text-slate-700 font-medium">{anomaly.count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Timestamp */}
          <p className="text-[10px] text-slate-400 pt-1">
            Updated {new Date(metrics.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      )}
    </div>
  );
}
